import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Checkbox, Col, Divider, Dropdown, Form, Input, InputNumber, message,
  Modal, Popconfirm, Row, Select, Space, Table, Tag, Typography,
} from 'antd';
import {
  CloseOutlined, MoreOutlined, PlusOutlined, SaveOutlined, EditOutlined, DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import useAuthStore from '../store/authStore';
import useScanTargetStore from '../store/scanTargetStore';
import useScanBufferStore from '../store/scanBufferStore';
import {
  ServiceOrder,
  ORDER_STATUS_LABELS, ORDER_STATUS_COLORS,
  getOrder, updateOrder, createOrder, setOrderType,
  ClientSearchItem, searchClients,
} from '../api/serviceOrders';
import { getOneCMarkStatus, getCatalogById, getOneCAcquiringTerminals } from '../api/onec';
import { markingApi } from '../api/marking';
import { unreceivedApi } from '../api/unreceived';
import type { BufferCode, GtinMarkItem } from '../api/marking';
import useIsMobile from '../hooks/useIsMobile';
import { autoServiceFirst, filtersFirst, filterWarehouseOptions, useOneCCatalogs } from '../hooks/useOneCCatalogs';
import DataMatrixImage from '../components/marking/DataMatrixImage';
import MarkCodeLink from '../components/marking/MarkCodeLink';
import { sellabilityError } from '../components/marking/sellable';
import { addBarcodeProductToLines } from '../components/service-order/barcode';
import type { BarcodeProduct } from '../api/barcodeScans';
import MarkPickerModal from '../components/marking/MarkPickerModal';
// Общие модули черновика заказ-наряда (ServiceOrderPage.md §2В, rev. 2026-09-08)
import {
  Draft, DraftLine, ItemType,
  ensureTrailingEmpty, isEmptyLine, isInfoRow, isPieceUnit, isVolumeUnit, displayUnit, fmtLitersFixed, newLine, withBulkInfoRow,
} from '../components/service-order/types';
import { emptyDraft, orderToDraft } from '../components/service-order/draft';
import { enterAsTab, arrowFieldNav, executorSelectKeyDown } from '../components/service-order/enterAsTab';
import { getOrderPostingAvailable, getOrderPrintAvailable, getReceiptFiscalizeAvailable, getTaxInvoiceAvailable, postOrderIn1C, fiscalizeReceipt } from '../components/service-order/posting';
import { printServiceOrderForm } from '../components/service-order/printing';
import { DeletionBanner, requestDeletionFlow } from '../components/service-order/deletion';
import { buildPayload, formatApiError } from '../components/service-order/buildPayload';
import ShiftClosedModal, { isShiftClosedError } from '../components/service-order/ShiftClosedModal';
import PostAndPayModal from '../components/service-order/PostAndPayModal';
import VehiclePicker from '../components/vehicle/VehiclePicker';
import PlateInput from '../components/vehicle/PlateInput';
// Единая логика разлива — общая с видом «Замена масла» (OilChangePage.md §1 п.4 rev. 2026-09-08 №2)
import {
  applyMarksToMaster, bulkCoverageError, bulkMasterMaxQty, fetchOwnProductMarks, fetchProductMarks,
  findRestorableBulkLines, promoteToBulkMaster, redistributeMaster,
} from '../components/service-order/bulk';

const { Title, Text } = Typography;

type ActiveFilter = 'all' | 'autoregium' | 'fomin';

// Статус марки в чеке ККМ 1С (реплика иконки формы ЧекККМ): ok — зелёная, mismatch/missing — красная
const MARK_STATUS_HINTS: Record<string, string> = {
  mismatch: 'В чеке 1С количество по маркам не совпадает с количеством товара — в 1С марка красная',
  missing: 'В чеке 1С по этому товару нет марок — в 1С строка красная',
};
const markStatusColor = (s?: string | null): string =>
  s === 'mismatch' || s === 'missing' ? '#ff4d4f' : '#52c41a';

const ServiceOrderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const activeFilter = (searchParams.get('filter') || 'all') as ActiveFilter;
  const navigate = useNavigate();
  const { hasPermission } = useAuthStore();
  const canWritePerm = hasPermission('orders:write');
  const isMobile = useIsMobile();

  const isNew = !id || id === 'new';
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  // Барьер «кассовая смена закрыта» (1c.md §1 rev. 21, rev. №19)
  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [order, setOrder] = useState<ServiceOrder | null>(null);
  // Помеченный на удаление / удалённый заказ — read-only (service_order_deletion.md §1, №29)
  const deletionPending = !!order?.deletion_state;
  // Проведённый заказ — read-only (ServiceOrderPage.md №30, синхронно с OilChangePage §5)
  const posted = order?.posted ?? false;
  const canWrite = canWritePerm && !deletionPending && !posted;
  const [warningMsg, setWarningMsg] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [highlightedKeys, setHighlightedKeys] = useState<Set<string>>(new Set());
  const [duplicateKeys, setDuplicateKeys] = useState<Set<string>>(new Set());
  const [selectedMarkCode, setSelectedMarkCode] = useState<string | null>(null);
  const [selectedMarkRawCode, setSelectedMarkRawCode] = useState<string | null>(null);
  const [loadingMarkRawCode, setLoadingMarkRawCode] = useState(false);
  const [markPickerKey, setMarkPickerKey] = useState<string | null>(null);
  const prevPartsRef = useRef<DraftLine[]>([]);
  // «Провести в 1С» в меню «⋯» (direct_posting.md §7 rev. №6)
  const [postingAvailable, setPostingAvailable] = useState(false);
  // «Пробить чек» — master + флаг receipt (direct_posting.md §7 rev. №8)
  const [fiscalizeAvailable, setFiscalizeAvailable] = useState(false);
  const [printAvailable, setPrintAvailable] = useState(false);
  const [invoiceAvailable, setInvoiceAvailable] = useState(false);
  const [postPayOpen, setPostPayOpen] = useState(false);

  useEffect(() => {
    if (isNew) return;
    getOrderPostingAvailable().then(setPostingAvailable);
    getReceiptFiscalizeAvailable().then(setFiscalizeAvailable);
    getOrderPrintAvailable().then(setPrintAvailable);
    getTaxInvoiceAvailable().then(setInvoiceAvailable);
  }, [isNew]);

  const load = useCallback(async () => {
    if (isNew || !id) return;
    setLoading(true);
    try {
      const o = await getOrder(Number(id));
      setOrder(o);
      setWarningMsg(o.warning || null);
      const d = orderToDraft(o);
      setDraft(d);
      prevPartsRef.current = d.parts; // не подсвечивать существующие строки
      // Догружаем марки разливных мастер-строк (остатки для перераспределения)
      d.parts.forEach(l => {
        if (l.is_bulk_master && l.external_1c_id) {
          loadBulkMarks(l.key, l.external_1c_id, o.id);
        }
      });
      // Восстанавливаем мастер разлива у литровых строк, сохранённых без марок (№20)
      restoreBulkMasters(d.parts, o.id);
    } catch (e: any) {
      message.error(formatApiError(e, 'Не удалось загрузить наряд'));
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    document.addEventListener('keydown', enterAsTab);
    document.addEventListener('keydown', arrowFieldNav, true);
    return () => {
      document.removeEventListener('keydown', enterAsTab);
      document.removeEventListener('keydown', arrowFieldNav, true);
    };
  }, []);

  // ── Сканы документов, привязанные к этому ЗН (через регистр documents)
  const [linkedScans, setLinkedScans] = useState<{
    id: number; title: string | null; number: string | null; date: string | null;
    type_name: string | null; page_count: number;
  }[]>([]);
  useEffect(() => {
    if (!id) { setLinkedScans([]); return; }
    api.get(`/service-orders/${id}/scans`)
      .then(r => setLinkedScans(r.data || []))
      .catch(() => setLinkedScans([]));
  }, [id]);

  // ── Подсветка новых запчастей (временная, до перезагрузки страницы)
  const HIGHLIGHT_DURATION_MS = 5000;
  const bufferItems = useScanBufferStore(s => s.items);
  // Марки буфера — оранжевый текст марки и штатное удаление крестом (rev. 2026-09-13)
  const bufferMarkingCodes = useMemo(
    () => new Set(bufferItems.map(s => s.marking_code).filter(Boolean) as string[]),
    [bufferItems],
  );
  useEffect(() => {
    const currentNonEmpty = draft.parts.filter(l => !isEmptyLine(l));

    // Находим все duplicate marking_code внутри draft.parts (всегда, не только новые)
    const codeToKeys = new Map<string, string[]>(); // marking_code -> keys[]
    currentNonEmpty.forEach(l => {
      if (l.marking_code) {
        const keys = codeToKeys.get(l.marking_code) || [];
        keys.push(l.key);
        codeToKeys.set(l.marking_code, keys);
      }
      (l.children || []).forEach(c => {
        if (c.marking_code) {
          const keys = codeToKeys.get(c.marking_code) || [];
          keys.push(c.key);
          codeToKeys.set(c.marking_code, keys);
        }
      });
    });

    const dupKeys = new Set<string>();
    codeToKeys.forEach(keys => {
      if (keys.length > 1) {
        keys.forEach(k => dupKeys.add(k));
      }
    });
    
    // Подсвечиваем строки, которые совпадают с марками в буфере сканирования
    const bufferMarkingCodes = new Set(bufferItems.map(s => s.marking_code).filter(Boolean) as string[]);
    bufferMarkingCodes.forEach(code => {
      const keys = codeToKeys.get(code) || [];
      keys.forEach(k => dupKeys.add(k));
    });
    
    setDuplicateKeys(dupKeys);

    // Зеленая подсветка для новых строк (которые появились сейчас)
    const prevKeys = new Set(prevPartsRef.current.map(l => l.key));
    const newLines = currentNonEmpty.filter(l => !prevKeys.has(l.key));
    if (newLines.length > 0) {
      const newKeys = newLines.map(l => l.key);
      setHighlightedKeys(prev => {
        const next = new Set(prev);
        newKeys.forEach(k => next.add(k));
        return next;
      });
      setTimeout(() => {
        setHighlightedKeys(prev => {
          const next = new Set(prev);
          newKeys.forEach(k => next.delete(k));
          return next;
        });
      }, HIGHLIGHT_DURATION_MS);
    }

    prevPartsRef.current = draft.parts;
  }, [draft.parts, bufferItems]);

  // ── ScanTarget: регистрация заказа-наряда как цели для буфера сканирования
  const draftRef = useRef(draft);
  useEffect(() => { draftRef.current = draft; }, [draft]);

  const setScanTarget = useScanTargetStore(s => s.setTarget);
  const setScanTargetMarkingCodes = useScanTargetStore(s => s.setMarkingCodes);
  const clearScanTarget = useScanTargetStore(s => s.clearTarget);

  // Обновляем markingCodes в store при изменении parts (для подсветки в буфере)
  useEffect(() => {
    const codes = new Set<string>();
    draft.parts.forEach(l => {
      if (!isEmptyLine(l) && l.marking_code) {
        codes.add(l.marking_code);
      }
      (l.children || []).forEach(c => {
        if (c.marking_code) codes.add(c.marking_code);
      });
    });
    setScanTargetMarkingCodes(codes);
  }, [draft.parts, setScanTargetMarkingCodes]);

  useEffect(() => {
    // rev. 2026-09-18: регистрируем и НОВЫЙ (несохранённый) ЗН — orderId тогда
    // null: checkCanAdd/resolveCode пропускаются, марка уходит сразу в черновик,
    // resolveCode выполняется после первого сохранения (handleSave)
    if (!canWrite) return;
    if (!order && !isNew) return;
    const orderId = order?.id ?? null;
    const orderNumber = order?.number ?? null;
    const orderLabel = orderNumber ? `ЗН № ${orderNumber}` : 'новый заказ-наряд';

    const handler = async (scan: BufferCode) => {
      console.log('[targetHandler] called with scan:', scan.marking_code, scan.raw_code);

      // Единое правило продажи (marking_buffer §3, rev. 2026-10-23): только ready;
      // разлив из буфера не добавляется, непродаваемые статусы — отказ с причиной
      const notSellable = sellabilityError(scan);
      if (notSellable) {
        return { ok: false, message: notSellable };
      }

      // Проверка дубля по marking_code (конкретная бутылка уже в заказе)
      const scanMarkingCode = scan.marking_code;
      if (scanMarkingCode) {
        const existingLine = draftRef.current.parts.find(
          l => !isEmptyLine(l) && (
            l.marking_code === scanMarkingCode ||
            (l.children || []).some(c => c.marking_code === scanMarkingCode)
          )
        );
        if (existingLine) {
          console.log('[targetHandler] duplicate found:', existingLine.name);
          return {
            ok: false,
            message: `Эта бутылка уже добавлена в ${orderLabel} (${existingLine.name})`,
            duplicate: true,
          };
        }
      }

      let markStatus = await getOneCMarkStatus(scan.marking_code || scan.raw_code);
      console.log('[targetHandler] markStatus:', markStatus);
      // Режим «без оприходования» (unreceived_sales.md §1А): марки нет в ШУТ 1С
      // (сбой ЭДО) — товар резолвит backend (марка или связка по фасовке),
      // позиция помечается unreceived_pending, split при сохранении исключит её
      let unreceivedDraft = false;
      if (!markStatus.found || !markStatus.product_id) {
        try {
          const { data: rd } = await unreceivedApi.resolveForDraft(scan.marking_code);
          markStatus = {
            found: true,
            mark_id: null,
            code: scan.marking_code,
            product_id: rd.product_id,
            product_name: rd.product_name || scan.product_name || undefined,
          };
          unreceivedDraft = true;
        } catch {
          // режим OFF или товар неизвестен — отказ ниже
        }
      }
      if (!markStatus.found || !markStatus.product_id) {
        return { ok: false, message: 'Марка не найдена в справочнике 1С' };
      }

      let price = 0;
      try {
        const catalogResult = await getCatalogById(markStatus.product_id);
        // /1c/catalog возвращает плоский массив (fix 2026-09-11: items?.[0] всегда
        // давал undefined → цена 0 у марок, добавленных сканом)
        const product = catalogResult[0];
        price = product?.price ?? 0;
      } catch {
        // Цену получить не удалось — добавляем с 0, пользователь поправит
      }

      const newPart: DraftLine = {
        key: Math.random().toString(36).slice(2),
        external_1c_id: markStatus.product_id,
        name: markStatus.product_name || 'Маркированный товар',
        quantity: 1,
        unit: 'шт',
        price: Number(price),
        marking_code: scan.marking_code,
        unreceived_pending: unreceivedDraft || undefined,
      };
      console.log('[targetHandler] newPart:', newPart);

      const isDuplicate = draftRef.current.parts.some(
        l => !isEmptyLine(l) && l.external_1c_id === newPart.external_1c_id
      );

      setDraft(d => {
        // Штучная марка — в мастер-группу товара (1 марка = 1 шт, rev. 2026-08-14).
        // Если мастер этого товара уже есть — добавляем дочернюю марку,
        // иначе создаём мастер с первой дочерней маркой.
        const parts = d.parts.filter(l => !isEmptyLine(l));
        const masterIdx = parts.findIndex(
          l => l.is_piece_master && l.external_1c_id === newPart.external_1c_id
        );
        if (masterIdx >= 0) {
          const m = parts[masterIdx];
          const child: DraftLine = {
            ...newPart,
            key: `${m.key}:${scan.marking_code}`,
            parent_key: m.key,
            quantity: 1,
          };
          parts[masterIdx] = {
            ...m,
            children: [...(m.children || []), child],
            quantity: (m.children?.length || 0) + 1,
          };
        } else {
          const master: DraftLine = {
            ...newPart,
            marking_code: null,
            is_piece_master: true,
            children: [{
              ...newPart,
              key: `${newPart.key}:${scan.marking_code}`,
              parent_key: newPart.key,
              quantity: 1,
            }],
          };
          parts.push(master);
        }
        const next = {
          ...d,
          parts: ensureTrailingEmpty(parts),
        };
        console.log('[targetHandler] setDraft next.parts length:', next.parts.length, 'last:', next.parts[next.parts.length-2]);
        return next;
      });

      return { ok: true, message: `${newPart.name} добавлен в ${orderLabel} (не забудьте сохранить)`, key: newPart.key, duplicate: isDuplicate };
    };

    const barcodeHandler = (product: BarcodeProduct) => {
      const result = addBarcodeProductToLines(draftRef.current.parts, product);
      if (result.ok) {
        setDraft(d => ({ ...d, parts: result.lines }));
        if (result.bulkProductId && result.key) {
          void loadBulkMarks(result.key, result.bulkProductId, orderId ?? undefined);
        }
      }
      return result;
    };

    setScanTarget(`В ${orderLabel}`, handler, orderId, orderNumber, barcodeHandler);
    // rev. 2026-08-18: target очищаем при размонтировании — автодобавление
    // из буфера работает только пока заказ-наряд реально открыт.
    return () => clearScanTarget();
  }, [isNew, order, canWrite, setScanTarget, clearScanTarget]);

  // ── Catalog sources (1C) — общий хук обоих видов (§2В; rev. 2026-09-19 №13):
  // ЗН — базовый вид, «Замена масла» — наследник; локальных загрузчиков каталогов
  // на странице нет (были дублями хука — фильтр «*»-услуг из-за этого не сработал)
  const {
    serviceOptions, productOptions, warehouseOptions,
    loadServices, loadProducts, loadWarehouse,
    debouncedLoadServices, debouncedLoadProducts,
  } = useOneCCatalogs();
  const [productSearch, setProductSearch] = useState('');
  const [clientOptions, setClientOptions] = useState<ClientSearchItem[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [personOptions, setPersonOptions] = useState<any[]>([]);
  const [openExecKey, setOpenExecKey] = useState<string | null>(null);

  // Обогащаем список опций текущими исполнителями из заказа,
  // чтобы теги Select multiple показывали имена, а не сырые ID
  const mergedPersonOptions = useMemo(() => {
    const map = new Map<string, any>(personOptions.map((o: any) => [o.value, o]));
    draft.services.forEach((l: DraftLine) => {
      if (l.executor1_id && !map.has(l.executor1_id)) {
        map.set(l.executor1_id, { value: l.executor1_id, label: l.executor1_name || l.executor1_id });
      }
      if (l.executor2_id && !map.has(l.executor2_id)) {
        map.set(l.executor2_id, { value: l.executor2_id, label: l.executor2_name || l.executor2_id });
      }
      if (l.executor3_id && !map.has(l.executor3_id)) {
        map.set(l.executor3_id, { value: l.executor3_id, label: l.executor3_name || l.executor3_id });
      }
    });
    return Array.from(map.values());
  }, [personOptions, draft.services]);

  // Обогащаем опции товаров/услуг позициями текущего заказа,
  // чтобы Select сразу показывал наименования, а не сырые GUID,
  // пока не догрузился каталог 1С (кэш onec_nomenclature, rev. 2026-08-18)
  const mergedServiceOptions = useMemo(() => {
    const map = new Map<string, any>(serviceOptions.map((o: any) => [o.value, o]));
    draft.services.forEach((l: DraftLine) => {
      if (l.external_1c_id && !map.has(l.external_1c_id)) {
        map.set(l.external_1c_id, { value: l.external_1c_id, label: l.name || l.external_1c_id, name: l.name, price: l.price });
      }
    });
    return Array.from(map.values());
  }, [serviceOptions, draft.services]);

  const mergedProductOptions = useMemo(() => {
    const map = new Map<string, any>(productOptions.map((o: any) => [o.value, o]));
    const addLine = (l: DraftLine) => {
      if (l.external_1c_id && !map.has(l.external_1c_id)) {
        map.set(l.external_1c_id, {
          value: l.external_1c_id,
          label: l.name || l.external_1c_id,
          name: l.name,
          price: l.price,
          unit: l.unit || 'шт',
        });
      }
    };
    draft.parts.forEach((l: DraftLine) => {
      addLine(l);
      (l.children || []).forEach(addLine);
    });
    return Array.from(map.values());
  }, [productOptions, draft.parts]);

  // Группа «Наш склад», отфильтрованная локально по введённому тексту
  const filteredWarehouseOptions = useMemo(
    () => filterWarehouseOptions(warehouseOptions, productSearch),
    [warehouseOptions, productSearch],
  );

  // Две группы одного селектора (rev. 2026-08-26, схема §2Б):
  // «Наш склад» (жидкости) + «Каталог 1С» (запчасти и всё остальное).
  // Дедупликация по GUID: позиция склада в группе 1С не дублируется.
  // Красные (markingExpected) — в конец своей группы (marking_piece §2.1 rev. 2026-09-07).
  // Фильтры первыми: масляный → воздушный → салонный → топливный (§2Б rev. 2026-09-15).
  const groupedProductOptions = useMemo(() => {
    const whIds = new Set(warehouseOptions.map(o => o.value));
    const catalogOpts = mergedProductOptions.filter(o => !whIds.has(o.value));
    return [
      { label: 'Наш склад', options: filtersFirst(filteredWarehouseOptions) },
      { label: 'Каталог 1С', options: filtersFirst(catalogOpts) },
    ];
  }, [warehouseOptions, filteredWarehouseOptions, mergedProductOptions]);

  const [terminalOptions, setTerminalOptions] = useState<any[]>([]);

  const loadClients = useCallback(async (q?: string) => {
    setLoadingClients(true);
    try {
      const items = await searchClients(q);
      setClientOptions(items);
    } finally {
      setLoadingClients(false);
    }
  }, []);

  const loadPersons = useCallback(async (q?: string) => {
    try {
      const r = await api.get('/1c/persons', { params: { search: q, grouped: 1 } });
      const items = Array.isArray(r.data) ? r.data : (r.data.persons || []);
      setPersonOptions(items.map((p: any) => ({
        value: p.id,
        label: p.name,
        name: p.name,
      })));
    } catch {
      // Игнорируем ошибку — исполнители необязательны
    }
  }, []);

  const loadTerminals = useCallback(async () => {
    try {
      const items = await getOneCAcquiringTerminals();
      setTerminalOptions(items.map((t: any) => ({
        value: t.id,
        label: t.name,
        name: t.name,
      })));
    } catch {
      // Игнорируем ошибку — терминал необязателен
    }
  }, []);

  useEffect(() => {
    // Каталог 1С больше не грузим целиком при открытии страницы (23k позиций, 3 МБ):
    // поиск идёт по локальному кэшу onec_nomenclature при вводе (debounce 300 мс),
    // имена текущего заказа подставлены через mergedServiceOptions/mergedProductOptions.
    loadClients();
    loadPersons();
    loadTerminals();
    loadWarehouse();
  }, [loadClients, loadPersons, loadTerminals, loadWarehouse]);



  // ── Helpers
  const lineTotal = (l: DraftLine): number => l.quantity * l.price;

  const totalServices = useMemo(
    () => draft.services.reduce((s, l) => s + lineTotal(l), 0),
    [draft.services],
  );
  const totalParts = useMemo(
    () => draft.parts.reduce((s, l) => s + lineTotal(l), 0),
    [draft.parts],
  );
  const grandTotal = totalServices + totalParts;
  // Отложенные позиции режима «без оприходования» (unreceived_sales.md v3)
  const unreceivedSum = draft.parts
    .filter(l => l.unreceived_pending && !l.parent_key)
    .reduce((sum, l) => sum + l.quantity * l.price, 0);

  const update = (patch: Partial<Draft>) => setDraft(d => ({ ...d, ...patch }));

  const updateLine = (type: ItemType, key: string, patch: Partial<DraftLine>) => {
    setDraft(d => {
      const src = type === 'service' ? d.services : d.parts;
      // mark_status описывает чек 1С «как прочитан» — любая правка строки его инвалидирует
      const updated = src.map(l => l.key === key ? { ...l, ...patch, mark_status: null } : l);
      return {
        ...d,
        [type === 'service' ? 'services' : 'parts']: ensureTrailingEmpty(updated),
      };
    });
  };

  const removeLine = (type: ItemType, key: string) => {
    setDraft(d => {
      const src = type === 'service' ? d.services : d.parts;
      const line = src.find(l => l.key === key);
      if (order?.id) {
        // Мгновенная отвязка марок при удалении строки сохранённого ЗН
        if (line?.marking_code) {
          markingApi.unbindCode(line.marking_code).catch(() => {});
        }
        if (line?.children) {
          line.children.forEach(c => {
            if (c.marking_code) markingApi.unbindCode(c.marking_code).catch(() => {});
          });
        }
      }
      const filtered = src.filter(l => l.key !== key);
      return {
        ...d,
        [type === 'service' ? 'services' : 'parts']: ensureTrailingEmpty(filtered),
      };
    });
  };

  // Удаление дочерней марки из мастер-строки (объём возвращается в остаток)
  const removeChildLine = (masterKey: string, childKey: string) => {
    setDraft(d => {
      const updated = d.parts.map(l => {
        if (l.key !== masterKey) return l;
        const child = (l.children || []).find(c => c.key === childKey);
        if (child?.marking_code && order?.id) {
          markingApi.unbindCode(child.marking_code).catch(() => {});
        }
        const children = (l.children || []).filter(c => c.key !== childKey);
        const total = Number(children.reduce((s, c) => s + c.quantity, 0).toFixed(1));
        return { ...l, children, quantity: total, mark_status: null };
      });
      return { ...d, parts: ensureTrailingEmpty(updated) };
    });
  };

  // Удаление штучной марки из piece-мастера (rev. 2026-09-13): штатно — крестом
  // (марка в буфере) или аварийно из модалки DataMatrix; мастер без марок удаляется
  const removePieceMark = (masterKey: string, childKey: string) => {
    setDraft(d => {
      const master = d.parts.find(l => l.key === masterKey);
      const child = master?.children?.find(c => c.key === childKey);
      if (child?.marking_code && order?.id) {
        markingApi.unbindCode(child.marking_code).catch(() => {});
      }
      const updated = d.parts
        .map(l => {
          if (l.key !== masterKey) return l;
          const children = (l.children || []).filter(c => c.key !== childKey);
          return { ...l, children, quantity: children.length, mark_status: null };
        })
        .filter(l => l.key !== masterKey || (l.children || []).length > 0);
      return { ...d, parts: ensureTrailingEmpty(updated) };
    });
  };

  const pickService = (key: string, val: string | null) => {
    if (val == null) { updateLine('service', key, { external_1c_id: null }); return; }
    const opt = mergedServiceOptions.find(o => o.value === val);
    if (!opt) return;
    const price = (opt as any).price ?? 0;

    setDraft(d => {
      const src = d.services;
      const curIdx = src.findIndex(l => l.key === key);
      const currentLine = curIdx >= 0 ? src[curIdx] : undefined;

      const basePatch: Partial<DraftLine> = {
        external_1c_id: opt.value,
        name: opt.name,
        unit: 'шт',
        price,
        recommended_price: price,
      };

      // Наследование исполнителей (rev. 2026-09-19 №11): строка без своих
      // исполнителей получает исполнителей ближайшей заполненной услуги ВЫШЕ.
      // Проверка по исполнителям, а не по isEmptyLine: поисковый ввод в селектор
      // уже успел записаться в name строки, и старая проверка «wasEmpty» не
      // срабатывала — наследование молча не происходило.
      const hasOwnExecutors = !!(
        currentLine &&
        (currentLine.executor1_id || currentLine.executor2_id || currentLine.executor3_id)
      );
      if (!hasOwnExecutors && curIdx > 0) {
        const above = src.slice(0, curIdx).filter(l => !isEmptyLine(l));
        const donor = above[above.length - 1] || null;
        if (donor) {
          basePatch.executor1_id = donor.executor1_id;
          basePatch.executor1_name = donor.executor1_name;
          basePatch.executor2_id = donor.executor2_id;
          basePatch.executor2_name = donor.executor2_name;
          basePatch.executor3_id = donor.executor3_id;
          basePatch.executor3_name = donor.executor3_name;
        }
      }

      const updated = src.map(l =>
        l.key === key ? { ...l, ...basePatch } : l
      );

      return {
        ...d,
        services: ensureTrailingEmpty(updated),
      };
    });
  };

  const pickProduct = (key: string, val: string | null) => {
    if (val == null) {
      updateLine('part', key, { external_1c_id: null });
      return;
    }
    // Группа «Наш склад» в приоритете — её опции несут те же поля (name = имя 1С)
    const opt = warehouseOptions.find(o => o.value === val) || mergedProductOptions.find(o => o.value === val);
    if (!opt) return;
    const price = (opt as any).price ?? 0;
    const unit = (opt as any).unit || 'шт';
    const volume = isVolumeUnit(unit);
    updateLine('part', key, {
      external_1c_id: opt.value,
      name: opt.name,
      unit,
      price: price,
      recommended_price: price,
      quantity: volume ? 0 : 1,
      marking_code: null,
      is_bulk_master: volume || undefined,
      children: volume ? [] : undefined,
      available_marks: null,
    });
    if (volume) {
      loadBulkMarks(key, opt.value, order?.id);
    }
  };

  // Загрузка марок разливного товара (rev. 2026-08-10: вместо несуществующего /1c/mark-balance)
  // serviceOrderId — контекст документа: available_volume марок этого ЗН включает взятый им объём
  const loadBulkMarks = async (key: string, productId: string, serviceOrderId?: number) => {
    try {
      const marks = await fetchProductMarks(productId, serviceOrderId);
      setDraft(d => ({ ...d, parts: ensureTrailingEmpty(applyMarksToMaster(d.parts, key, marks)) }));
    } catch {
      message.error('Не удалось загрузить марки товара');
    }
  };

  // rev. 2026-09-30 (№20, §2В): плоская литровая строка без марки (мастер разлива,
  // сохранённый без марок как предварительный документ) восстанавливается мастером —
  // иначе авто-распределение не работало и объём уходил в 1С без марки (красная в чеке)
  const restoreBulkMasters = async (parts: DraftLine[], serviceOrderId?: number) => {
    for (const l of findRestorableBulkLines(parts)) {
      try {
        const marks = await fetchOwnProductMarks(l.external_1c_id!, serviceOrderId);
        if (marks.length > 0) {
          setDraft(d => ({ ...d, parts: ensureTrailingEmpty(promoteToBulkMaster(d.parts, l.key, marks)) }));
        }
      } catch { /* тихо: строка остаётся плоской */ }
    }
  };

  // changeMasterQty/updateChildVolume/toggleChildManual/addManualChild — обёртки над
  // общей логикой разлива (components/service-order/bulk.ts)

  // Экстренная правка объёма дочерней марки (схема 3.1) — мастер пересчитывается как сумма
  const updateChildVolume = (masterKey: string, childKey: string, newQty: number) => {
    setDraft(d => {
      const updated = d.parts.map(l => {
        if (l.key !== masterKey) return l;
        const children = (l.children || []).map(c =>
          c.key === childKey ? { ...c, quantity: newQty, mark_status: null } : c
        );
        const total = Number(children.reduce((s, c) => s + c.quantity, 0).toFixed(1));
        return { ...l, children, quantity: total, mark_status: null };
      });
      return { ...d, parts: ensureTrailingEmpty(updated) };
    });
  };

  const toggleChildManual = (masterKey: string, childKey: string) => {
    setDraft(d => ({
      ...d,
      parts: ensureTrailingEmpty(d.parts.map(l =>
        l.key === masterKey
          ? { ...l, children: (l.children || []).map(c => c.key === childKey ? { ...c, manual_volume: !c.manual_volume } : c) }
          : l
      )),
    }));
  };

  // Изменение объёма мастер-строки (авто-режим) — перераспределение по маркам
  const changeMasterQty = (masterKey: string, newQty: number) => {
    setDraft(d => ({
      ...d,
      parts: ensureTrailingEmpty(d.parts.map(l =>
        l.key === masterKey ? { ...redistributeMaster(l, newQty), mark_status: null } : l
      )),
    }));
  };

  const addManualChild = (masterKey: string, mark: GtinMarkItem) => {
    setDraft(d => {
      const master = d.parts.find(l => l.key === masterKey);
      if (master && (master.children || []).some(c => c.marking_code === mark.marking_code)) {
        message.warning('Эта марка уже в строке товара');
        return d;
      }
      const updated = d.parts.map(l => {
        if (l.key !== masterKey) return l;
        const children = [
          ...(l.children || []),
          {
            key: `${masterKey}:${mark.marking_code}`,
            external_1c_id: l.external_1c_id,
            name: l.name,
            quantity: 0,
            unit: l.unit,
            price: l.price,
            marking_code: mark.marking_code,
            parent_key: masterKey,
            available_volume: mark.available_volume != null ? Number(mark.available_volume) : null,
            manual_volume: true,
          },
        ];
        return { ...l, children };
      });
      return { ...d, parts: ensureTrailingEmpty(updated) };
    });
  };

  const pickClient = (key: string | null) => {
    if (!key || key === '__current__') {
      if (!key) update({ client_type: null, client_id: null, client_label: null, retail_buyer_name: '' });
      return;
    }
    const [type, idStr] = key.split(':');
    const id = Number(idStr);
    const opt = clientOptions.find(c => c.type === type && c.id === id);
    if (!opt) return;
    update({
      client_type: opt.type,
      client_id: opt.id,
      client_label: opt.label,
      // Физлицо → ФИО в retail_buyer_name; юрлицо → "" (очистка ФИО в 1С)
      // (схема ServiceOrderPage.md §2А, rev. 2026-08-19)
      retail_buyer_name: opt.type === 'individual' ? opt.label : '',
      // Оплата по типу клиента (rev. 2026-09-17 №8): юрлицо — «Банк» без карты,
      // физлицо — «Касса» + карта. Поля редактируемы — нал/карта юрлицу штатно.
      payment_type: opt.type === 'counterparty' ? 'Банк' : 'Касса',
      card_payment: opt.type !== 'counterparty',
    });
  };


  // ── Save
  // allowClosedShift — экстренный обход барьера «смена закрыта» (rev. №19):
  // true только из ShiftClosedModal по долгому удержанию кнопки
  const doSave = async (allowClosedShift = false, cardPaymentOverride?: boolean): Promise<boolean> => {
    // Валидация разливных мастер-строк: объём должен быть покрыт марками
    const badMaster = bulkCoverageError(draft.parts, isEmptyLine);
    if (badMaster) {
      message.error(`«${badMaster.name}»: объём ${badMaster.quantity} ${badMaster.unit} не покрыт марками — недостаточно остатка`);
      return false;
    }
    setSaving(true);
    try {
      const draftToSave: Draft = {
        ...draft,
        ...(cardPaymentOverride !== undefined ? { card_payment: cardPaymentOverride } : {}),
      };
      const payload = buildPayload(draftToSave);
      if (isNew) {
        const created = await createOrder({
          vehicle_id: payload.vehicle_id,
          vehicle_make: payload.vehicle_make,
          vehicle_model: payload.vehicle_model,
          license_plate: payload.license_plate,
          vin: payload.vin,
          manufacture_year: payload.manufacture_year,
          color: payload.color,
          client_type: payload.client_type,
          client_id: payload.client_id,
          retail_buyer_name: payload.retail_buyer_name,
          mileage: payload.mileage,
          recommendations: payload.recommendations,
          prepayment_sum: payload.prepayment_sum,
          work_start_date: payload.work_start_date,
          work_end_date: payload.work_end_date,
          items: payload.items,
          payment_type: payload.payment_type,
          card_payment: payload.card_payment,
          allow_closed_shift: allowClosedShift,
        });
        message.success(`Наряд ${created.number} создан`);
        if (created.warning) message.warning(created.warning, 8);
        // rev. 2026-09-18: марки, добавленные сканом в новый ЗН, оставались в
        // буфере (orderId был null) — резолвим их с id созданного заказа
        const draftMarkCodes = new Set<string>();
        draftToSave.parts.forEach(l => {
          if (!isEmptyLine(l) && l.marking_code) draftMarkCodes.add(l.marking_code);
          (l.children || []).forEach(c => { if (c.marking_code) draftMarkCodes.add(c.marking_code); });
        });
        for (const mc of Array.from(draftMarkCodes)) {
          const scan = bufferItems.find(s => s.marking_code === mc);
          if (!scan || scan.product_type === 'bulk') continue;
          try {
            await markingApi.resolveCode(mc, {
              action: 'sale',
              order_id: created.id,
              order_number: created.number,
            });
          } catch { /* марка уже не в буфере — не мешает сохранению */ }
        }
        useScanBufferStore.getState().refreshItems();
        navigate(`/service-orders/${created.id}`, { replace: true });
        return true;
      } else if (id) {
        const updated = await updateOrder(Number(id), { ...payload, base_version: order?.content_version, allow_closed_shift: allowClosedShift });
        setOrder(updated);
        const d = orderToDraft(updated);
        setDraft(d);
        // orderToDraft сбрасывает available_marks/available_volume (null) —
        // без догрузки потолки объёма схлопываются до текущих значений
        d.parts.forEach(l => {
          if (l.is_bulk_master && l.external_1c_id) {
            loadBulkMarks(l.key, l.external_1c_id, updated.id);
          }
        });
        restoreBulkMasters(d.parts, updated.id);
        message.success('Сохранено');
        if (updated.warning) message.warning(updated.warning, 8);
        return true;
      }
    } catch (e: any) {
      // 409 shift_not_open (1c.md §1 rev. 21, rev. №19): кассовая смена закрыта —
      // окно с экстренным обходом по долгому удержанию кнопки
      if (isShiftClosedError(e)) {
        setShiftModalOpen(true);
      } else if (e?.response?.status === 409 && id) {
        // 409 — конкурентное сохранение (1c.md §3 rev. 19): показать, кто изменил,
        // и предложить осознанно загрузить свежую версию (свои правки теряются)
        Modal.warning({
          title: 'Заказ изменён другим пользователем',
          content: typeof e?.response?.data?.detail === 'string'
            ? e.response.data.detail
            : 'Пока вы редактировали, заказ был сохранён другим пользователем.',
          okText: 'Загрузить свежую версию',
          onOk: () => load(),
        });
      } else {
        message.error(formatApiError(e));
      }
    } finally {
      setSaving(false);
    }
    return false;
  };

  // Сохранение с услугой без исполнителя — неблокирующее инфо-окно (rev. №14):
  // напоминание, не принуждение — после «ОК» сохранение продолжается
  const handleSave = () => {
    const missingExecutor = draft.services.some(
      l => !isEmptyLine(l) && !l.executor1_id && !l.executor2_id && !l.executor3_id,
    );
    if (missingExecutor) {
      Modal.info({
        title: 'Не установлен исполнитель',
        content: 'В одной или нескольких услугах не указан исполнитель.',
        okText: 'ОК',
        onOk: () => { void doSave(); },
      });
      return;
    }
    void doSave();
  };

  // ── Renderers
  const isSectionDimmed = (section: ItemType) => {
    if (activeFilter === 'all') return false;
    if (activeFilter === 'autoregium') return section === 'service';
    if (activeFilter === 'fomin') return section === 'part';
    return false;
  };

  const renderLinesTable = (type: ItemType, lines: DraftLine[], total: number) => {
    const dimmed = isSectionDimmed(type);
    const title = type === 'service' ? 'Услуги' : 'Запчасти';
    // Марки — компактные подстроки мастера (rev. 2026-09-13 №3): children
    // остаются в dataSource, мастер однострочный.
    // Мастер разлива получает инфо-подстроку первым ребёнком (rev. 2026-09-12 №23)
    const tableData = lines.map(withBulkInfoRow);

    return (
      <div style={{ marginBottom: 16, opacity: dimmed ? 0.5 : 1 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 8,
            padding: '0 4px',
          }}
        >
          <Space size={8}>
            <Text strong style={{ fontSize: 15 }}>{title}</Text>
            {dimmed && <Tag>Не относится к текущему виду</Tag>}
          </Space>
        </div>
        <Table
          size="small"
          rowKey="key"
          pagination={false}
          showHeader={true}
          dataSource={tableData}
          expandable={{
            // Марки всегда раскрыты: expandedRowKeys управляемые — async-догрузка марок
            // после mount не раскрывалась defaultExpandAllRows, а иконка «+» упразднена
            expandedRowKeys: tableData.filter(r => r.children && r.children.length > 0).map(r => r.key),
            rowExpandable: (r: DraftLine) => !!(r.children && r.children.length > 0),
            showExpandColumn: false,
          }}
          locale={{ emptyText: dimmed ? '—' : 'Нет позиций' }}
          className={type === 'service' ? 'order-services-table order-no-expand-icon' : 'order-no-expand-icon'}
          rowClassName={(l: DraftLine) => (isInfoRow(l) ? 'order-info-subrow' : l.parent_key ? 'order-mark-subrow' : '')}
          onRow={(record) => ({
            style: duplicateKeys.has(record.key)
              ? { animation: 'glow-pulse-orange 1.5s ease-in-out infinite', outline: '2px solid #faad14', outlineOffset: '-2px', borderRadius: 4 }
              : undefined,
          })}
          columns={[
            {
              title: type === 'service' ? 'Услуга' : 'Запчасть', key: 'item',
              render: (_: unknown, l: DraftLine) => {
                // Инфо-подстрока разлива: ячейка товара пустая (rev. 2026-09-12 №23)
                if (isInfoRow(l)) return null;
                if (type === 'service') {
                  return (
                    <Select
                      showSearch allowClear filterOption={false}
                      defaultActiveFirstOption
                      placeholder="Введите название услуги"
                      style={{ width: '100%' }}
                      value={l.external_1c_id ?? undefined}
                      onSearch={(q) => {
                        debouncedLoadServices(q);
                        if (q.trim()) updateLine('service', l.key, { name: q.trim(), external_1c_id: null });
                      }}
                      onDropdownVisibleChange={(v) => { if (v) loadServices(); }}
                      onChange={(v) => {
                        if (!v) {
                          if (!isEmptyLine(l)) removeLine('service', l.key);
                          return;
                        }
                        pickService(l.key, v as string);
                      }}
                      // Авто-услуги первыми (бесплатная → платная), далее исходный
                      // порядок; «*»-позиции 1С скрыты в loadServices (rev. 2026-09-19 №13)
                      options={autoServiceFirst(mergedServiceOptions)}
                      disabled={dimmed || !canWrite}
                      notFoundContent="Начните вводить"
                    />
                  );
                }
                return (
                  <div style={
                    highlightedKeys.has(l.key)
                      ? { outline: '2px solid #52c41a', outlineOffset: 2, borderRadius: 4 }
                      : undefined
                  }>
                    {l.parent_key ? (
                      !isVolumeUnit(l.unit) ? (
                        // Подстрока штучной марки (rev. 2026-09-13 №3): MarkCodeLink —
                        // клик → DataMatrix, крест при марке в буфере
                        <MarkCodeLink
                          markingCode={l.marking_code!}
                          statusHint={l.mark_status ? MARK_STATUS_HINTS[l.mark_status] : undefined}
                          statusError={!!l.mark_status && l.mark_status !== 'ok'}
                          inBuffer={bufferMarkingCodes.has(l.marking_code!)}
                          hasFullCode={l.mark_has_full_code ?? undefined}
                          onRemove={canWrite && !dimmed ? () => removePieceMark(l.parent_key!, l.key) : undefined}
                        />
                      ) : (
                      // Подстрока марки разлива
                      <div
                        style={{
                          fontSize: 12,
                          color:
                            // Марка без криптохвоста — красная (rev. 2026-09-17 №7), приоритет
                            l.mark_has_full_code === false
                              ? '#ff4d4f'
                              : l.mark_status === 'mismatch' || l.mark_status === 'missing'
                                ? markStatusColor(l.mark_status)
                                : l.marking_code && bufferMarkingCodes.has(l.marking_code)
                                  ? '#fa8c16'
                                  : markStatusColor(l.mark_status),
                          wordBreak: 'break-all',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                        title={
                          l.mark_has_full_code === false
                            ? 'Марка без криптохвоста — отсканируйте товар сканером, иначе заказ не сохранить'
                            : l.mark_status ? MARK_STATUS_HINTS[l.mark_status] : undefined
                        }
                        onClick={async () => {
                          setSelectedMarkCode(l.marking_code!);
                          setSelectedMarkRawCode(null);
                          setLoadingMarkRawCode(true);
                          try {
                            const { data } = await markingApi.getCodeByMarkingCode(l.marking_code!);
                            setSelectedMarkRawCode(data.raw_code);
                          } catch (e: any) {
                            message.error(e?.response?.data?.detail || 'Не удалось загрузить полный код марки');
                          } finally {
                            setLoadingMarkRawCode(false);
                          }
                        }}
                      >
                        Марка: {l.marking_code}
                      </div>
                      )
                    ) : l.is_piece_master ? (
                      // Мастер штучного маркированного товара (rev. 2026-09-13 №3):
                      // однострочный — товар определяется марками, марки — подстроками ниже
                      <div>
                        <Text
                          style={l.mark_status && l.mark_status !== 'ok' ? { color: '#ff4d4f' } : undefined}
                          title={l.mark_status ? MARK_STATUS_HINTS[l.mark_status] : undefined}
                        >
                          {l.name}
                        </Text>
                      </div>
                    ) : (
                      <>
                        <Space.Compact style={{ width: '100%' }}>
                          <Select
                            showSearch allowClear filterOption={false}
                            defaultActiveFirstOption={false}
                            placeholder="Склад / 1С"
                            style={{ width: '100%' }}
                            value={l.external_1c_id ?? undefined}
                            onSearch={(q) => { setProductSearch(q); debouncedLoadProducts(q); }}
                            onDropdownVisibleChange={(v) => { if (v) { setProductSearch(''); loadProducts(); } }}
                            onChange={(v) => {
                              if (v == null) {
                                if (!isEmptyLine(l)) removeLine('part', l.key);
                                return;
                              }
                              pickProduct(l.key, v as string);
                            }}
                            options={groupedProductOptions}
                            optionRender={(opt) =>
                              (opt.data as any).markingExpected ? (
                                <div
                                  style={{ background: '#fff1f0', margin: '-5px -12px', padding: '5px 12px' }}
                                  title="Товар должен быть маркированным, но в 1С признак маркировки не установлен"
                                >
                                  {(opt.data as any).label}
                                  <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6 }}>
                                    в 1С нет признака маркировки
                                  </span>
                                </div>
                              ) : (
                                <>{(opt.data as any).label}</>
                              )
                            }
                            disabled={dimmed || !canWrite}
                            notFoundContent="Не найдено"
                          />
                          {l.is_bulk_master && canWrite && !dimmed && (
                            <Button
                              size="small"
                              icon={<EditOutlined />}
                              title="Выбрать марку вручную (экстренный случай)"
                              onClick={() => setMarkPickerKey(l.key)}
                            />
                          )}
                        </Space.Compact>
                        {l.is_bulk_master && l.available_marks && l.available_marks.length === 0 && (
                          <div style={{ fontSize: 11, color: '#faad14', marginTop: 2 }}>
                            Марки этого товара не найдены — отсканируйте бочку сканером
                          </div>
                        )}
                        {!l.marking_code && (l.mark_status === 'missing' || l.mark_status === 'mismatch') && (
                          <div style={{ fontSize: 11, color: '#ff4d4f', marginTop: 2 }}>
                            {MARK_STATUS_HINTS[l.mark_status]}
                          </div>
                        )}
                      </>
                    )}
                    {!l.is_bulk_master && !l.parent_key && l.marking_code && (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 12,
                          color: markStatusColor(l.mark_status),
                          wordBreak: 'break-all',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                        title={l.mark_status ? MARK_STATUS_HINTS[l.mark_status] : undefined}
                        onClick={async () => {
                          setSelectedMarkCode(l.marking_code!);
                          setSelectedMarkRawCode(null);
                          setLoadingMarkRawCode(true);
                          try {
                            const { data } = await markingApi.getCodeByMarkingCode(l.marking_code!);
                            setSelectedMarkRawCode(data.raw_code);
                          } catch (e: any) {
                            message.error(e?.response?.data?.detail || 'Не удалось загрузить полный код марки');
                          } finally {
                            setLoadingMarkRawCode(false);
                          }
                        }}
                      >
                        Марка: {l.marking_code}
                      </div>
                    )}
                    {l.unreceived_pending && !l.parent_key && (
                      <div style={{ marginTop: 4 }}>
                        <Tag
                          color="orange"
                          style={{ marginInlineEnd: 0 }}
                          title="Марки нет в 1С (сбой ЭДО) — позиция не отправлена в 1С; дофискализация вручную после приёмки УПД"
                        >
                          ожидает оприходования
                        </Tag>
                      </div>
                    )}
                  </div>
                );
              },
            },
            {
              title: 'Кол-во', dataIndex: 'quantity', key: 'qty', width: 150,
              align: 'center' as const,
              render: (v: number, l: DraftLine) => {
                // Инфо-подстрока разлива (rev. 2026-09-12 №23): «Доступно: X л» —
                // относится ко всему товару, рендерится сразу под мастером (первый ребёнок)
                if (isInfoRow(l)) {
                  return (
                    <div style={{ fontSize: 11, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
                      {l.available_marks != null ? `Доступно: ${fmtLitersFixed(bulkMasterMaxQty(l))} ${displayUnit(l.unit)}` : ''}
                    </div>
                  );
                }
                // Пустая строка (позиция не выбрана) — без контролов (rev. 2026-09-19 №12,
                // синхронно с OilChangePage §3А)
                if (isEmptyLine(l)) return null;
                // Подстрока штучной марки (rev. 2026-09-13 №3): Кол-во пустое —
                // удаление только крестом MarkCodeLink в колонке товара
                if (l.parent_key && !isVolumeUnit(l.unit)) {
                  return null;
                }
                // Дочерняя строка марки разлива: объём не редактируется (экстренная правка — по иконке)
                if (l.parent_key) {
                  // available_volume — потолок марки (включает текущее quantity), не растёт при вводе
                  const maxChild = l.available_volume ?? v;
                  return (
                    <Space size={4}>
                      <InputNumber
                        min={0}
                        max={maxChild}
                        step={0.1}
                        precision={1}
                        decimalSeparator=","
                        value={v}
                        disabled={dimmed || !canWrite || !l.manual_volume}
                        onChange={(val) => {
                          const numVal = Number(val) || 0;
                          updateChildVolume(l.parent_key!, l.key, Math.min(numVal, maxChild));
                        }}
                        style={{ width: 80 }}
                        className="order-input-centered"
                      />
                      {canWrite && !dimmed && !l.manual_volume && (
                        <Button
                          size="small"
                          type="text"
                          icon={<EditOutlined />}
                          title="Экстренная правка объёма марки"
                          onClick={() => toggleChildManual(l.parent_key!, l.key)}
                        />
                      )}
                      {canWrite && !dimmed && (
                        <Button
                          size="small"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          title="Убрать марку из документа"
                          onClick={() => removeChildLine(l.parent_key!, l.key)}
                        />
                      )}
                    </Space>
                  );
                }
                // Мастер-строка штучного маркированного товара: количество = число марок,
                // не редактируется ни в коем случае (меняется добавлением/удалением марок)
                if (l.is_piece_master) {
                  return <Text>{l.children?.length ?? v} шт</Text>;
                }
                // Мастер-строка разливного товара: единственное место авто-изменения объёма.
                // «Доступно: X л» — на инфо-подстроке под мастером (rev. 2026-09-12 №23)
                if (l.is_bulk_master) {
                  const maxQty = bulkMasterMaxQty(l);
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                      <InputNumber
                        min={0}
                        max={maxQty}
                        step={0.1}
                        precision={1}
                        decimalSeparator=","
                        value={v || null}
                        onChange={(val) => changeMasterQty(l.key, Number(val) || 0)}
                        disabled={dimmed || !canWrite}
                        style={{ width: '100%' }}
                        className="order-input-centered"
                      />
                    </div>
                  );
                }
                const piece = isPieceUnit(l.unit);
                const isMarkedPiece = !!(type === 'part' && l.marking_code && piece);
                const canEditQty = !isMarkedPiece;
                const minVal = piece ? 1 : 0;
                const stepVal = piece ? 1 : 0.1;
                const precVal = piece ? 0 : 1;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                    <InputNumber
                      min={minVal}
                      step={stepVal}
                      precision={precVal}
                      decimalSeparator={piece ? undefined : ','}
                      value={v || null}
                      onChange={(val) => {
                        if (!canEditQty) return;
                        const numVal = Number(val) || 0;
                        updateLine(type, l.key, { quantity: numVal || (piece ? 1 : 0) });
                      }}
                      disabled={dimmed || !canWrite || !canEditQty}
                      style={{ width: '100%' }}
                      className="order-input-centered"
                    />
                  </div>
                );
              },
            },
            {
              title: 'Цена, ₽', dataIndex: 'price', key: 'price', width: 160,
              align: 'center' as const,
              render: (v: number, l: DraftLine) => {
                if (l.parent_key) return null; // дочерняя марка — цена у мастер-строки
                // Пустая строка — без контролов (rev. 2026-09-19 №12)
                if (isEmptyLine(l)) return null;
                return (
                <div>
                  <InputNumber
                    min={0} step={50} precision={2} decimalSeparator="," value={v || null}
                    onChange={(val) => updateLine(type, l.key, { price: Number(val) })}
                    disabled={dimmed || !canWrite}
                    style={{ width: '100%' }}
                    className="order-input-centered"
                  />
                  {l.recommended_price !== undefined && l.recommended_price !== 0 && l.recommended_price !== v && (
                    <div style={{ fontSize: 11, color: l.price > l.recommended_price ? '#ff4d4f' : '#52c41a', marginTop: 2 }}>
                      Рек.: {l.recommended_price.toLocaleString('ru-RU')} ₽
                    </div>
                  )}
                </div>
                );
              },
            },
            {
              title: 'Сумма', key: 'sum', width: 100, align: 'right' as const,
              render: (_: unknown, l: DraftLine) => {
                if (l.parent_key) return null; // дочерняя марка — сумма у мастер-строки
                // Пустая строка — без суммы (rev. 2026-09-19 №12)
                if (isEmptyLine(l)) return null;
                return <b>{(l.quantity * l.price).toLocaleString('ru-RU')} ₽</b>;
              },
            },
              ...(type === 'service' ? [{
              title: 'Исполнитель', key: 'executor', width: 280,
              render: (_: unknown, l: DraftLine) => {
                // Пустая хвостовая строка — без селектора исполнителей (rev. 2026-09-30 №25):
                // updateLine → ensureTrailingEmpty фильтрует пустые по isEmptyLine (имя/external_1c_id,
                // исполнители не учитываются) и заменяет строку свежей — выбор молча затирался
                if (isEmptyLine(l)) return null;
                const executorIds = [
                  l.executor1_id,
                  l.executor2_id,
                  l.executor3_id,
                ].filter((id): id is string => !!id);
                return (
                  <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    filterOption={false}
                    placeholder="Исполнитель"
                    style={{ width: '100%' }}
                    value={executorIds}
                    onSearch={(q) => loadPersons(q)}
                    onChange={(v) => {
                      const ids = (v as string[]) || [];
                      const nameOf = (id: string | undefined): string | null =>
                        id ? ((mergedPersonOptions.find((o: any) => o.value === id)?.label as string) ?? null) : null;
                      updateLine('service', l.key, {
                        executor1_id: ids[0] ?? null,
                        executor1_name: nameOf(ids[0]),
                        executor2_id: ids[1] ?? null,
                        executor2_name: nameOf(ids[1]),
                        executor3_id: ids[2] ?? null,
                        executor3_name: nameOf(ids[2]),
                      });
                    }}
                    options={mergedPersonOptions}
                    disabled={dimmed || !canWrite}
                    notFoundContent="Начните вводить"
                    open={openExecKey === l.key}
                    onFocus={() => { if (!dimmed && canWrite) setOpenExecKey(l.key); }}
                    onDropdownVisibleChange={(v) => setOpenExecKey(v ? l.key : null)}
                    onInputKeyDown={(e) => executorSelectKeyDown(e, mergedPersonOptions, executorIds)}
                  />
                );
              },
            }] : []),
          ]}
        />
        {/* Сумма раздела — строка «Итого» под таблицей (rev. 2026-09-26 №18) */}
        <div style={{ textAlign: 'right', marginTop: 4 }}>
          <Text strong>Итого: {total.toLocaleString('ru-RU')} ₽</Text>
        </div>
      </div>
    );
  };

  const vehicleCaption = useMemo(() => {
    const parts: string[] = [];
    if (draft.license_plate) parts.push(draft.license_plate);
    if (draft.vehicle) parts.push(draft.vehicle);
    return parts.join(' · ');
  }, [draft.license_plate, draft.vehicle]);

  if (loading) return <div style={{ padding: 24 }}><Text>Загрузка...</Text></div>;

  return (
    <>
      <style>{`
        @keyframes glow-pulse-orange {
          0% { box-shadow: 0 0 0 0 rgba(250, 173, 20, 0.7); }
          70% { box-shadow: 0 0 0 10px rgba(250, 173, 20, 0); }
          100% { box-shadow: 0 0 0 0 rgba(250, 173, 20, 0); }
        }
      `}</style>
      {warningMsg && (
        <Alert
          message={warningMsg}
          type="warning"
          showIcon
          closable
          onClose={() => setWarningMsg(null)}
          style={{ marginBottom: 12 }}
        />
      )}
      {/* Пометка на удаление — баннер + отмена (service_order_deletion.md, №29) */}
      {!isNew && order && (
        <DeletionBanner order={order} canWrite={canWritePerm} onCancelled={load} />
      )}
      {/* ── Шапка */}
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: 12, padding: '0 4px' }}
        gutter={[8, 8]}
        wrap={false}
      >
        <Col flex="none">
          <Space wrap>
            <Button icon={<CloseOutlined />} onClick={() => navigate('/service-orders')}>Закрыть</Button>
            <Title level={4} style={{ margin: 0 }}>
              {isNew ? 'Новый наряд' : `ЗН № ${order?.number}`}
            </Title>
            {!isNew && order && (
              <>
                <Tag color={ORDER_STATUS_COLORS[order.status]} style={{ fontSize: 14, padding: '4px 12px' }}>
                  {ORDER_STATUS_LABELS[order.status]}
                </Tag>
                {order.posted && (
                  <Tag color="green" style={{ fontSize: 14, padding: '4px 12px' }}>
                    Проведён
                  </Tag>
                )}
                {deletionPending && (
                  <Tag
                    color={order?.deletion_state === 'done' ? 'default' : 'red'}
                    style={{ fontSize: 14, padding: '4px 12px' }}
                  >
                    {order?.deletion_state === 'done' ? 'удалён' : 'на удаление'}
                  </Tag>
                )}
              </>
            )}
          </Space>
        </Col>
        {!isMobile && vehicleCaption && (
          <Col flex="auto" style={{ textAlign: 'center', minWidth: 0, padding: '0 16px' }}>
            <Text
              strong
              style={{
                fontSize: 16,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'inline-block',
                maxWidth: '100%',
              }}
            >
              {vehicleCaption}
            </Text>
          </Col>
        )}
        <Col flex="none">
          <Space>
            {!isNew && !deletionPending && order && (postingAvailable || fiscalizeAvailable || printAvailable || invoiceAvailable) && (
              <Button onClick={() => setPostPayOpen(true)}>Провести и оплатить</Button>
            )}
            {canWrite && (
              <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
                Сохранить
              </Button>
            )}
            <Dropdown
              menu={{
                items: [
                  // «Провести в 1С» — заказ не завершён (№12: проведение доводит ЗН до
                  // «Выполнено») + флаги (direct_posting.md §7 rev. №6, №12).
                  // Без ЗН Фомин (заказ «только запчасти», 1c.md §1 rev. 25) проводить нечего — скрываем.
                  ...(!isNew && order && order.external_1c_id && !(order.posted && order.status === 'Выполнено') && postingAvailable
                    ? [{ key: 'post-1c', label: 'Провести в 1С' }]
                    : []),
                  // «Пробить чек товаров» — чек АР создан и не пробит (direct_posting.md §7 rev. №8, №11)
                  ...(!isNew && order && order.external_1c_cash_receipt_id && order.receipt_posted === false && fiscalizeAvailable
                    ? [{ key: 'fiscalize', label: 'Пробить чек товаров' }]
                    : []),
                  // «Пробить чек услуг» — чек Фомина обнаружен и не пробит (direct_posting.md §7 rev. №11)
                  ...(!isNew && order && order.external_1c_service_receipt_id && order.service_receipt_posted === false && fiscalizeAvailable
                    ? [{ key: 'fiscalize-service', label: 'Пробить чек услуг' }]
                    : []),
                  // Прямая печать через 1С без браузерного PDF (№35)
                  ...(!isNew && order && order.external_1c_id
                    ? [{ key: 'print-order', label: 'Печать заказ-наряда', disabled: !printAvailable }]
                    : []),
                  // Вид замены масла — только для розницы (OilChangePage.md §1 п.7, rev. 2026-09-08)
                  {
                    key: 'oil-change',
                    label: 'Показать в виде замены масла',
                    disabled: draft.client_type === 'counterparty',
                  },
                  // «Удалить» — двухстадийное удаление (service_order_deletion.md, №29)
                  ...(!isNew && order && !deletionPending && canWritePerm
                    ? [{ key: 'delete', label: 'Удалить', danger: true }]
                    : []),
                ],
                onClick: ({ key }) => {
                  if (key === 'post-1c') {
                    postOrderIn1C(Number(id), load);
                    return;
                  }
                  if (key === 'fiscalize') {
                    fiscalizeReceipt(Number(id), { cardPayment: !!order?.card_payment, receiptKind: 'goods' }, load);
                    return;
                  }
                  if (key === 'fiscalize-service') {
                    fiscalizeReceipt(Number(id), { cardPayment: !!order?.card_payment, receiptKind: 'services' }, load);
                    return;
                  }
                  if (key === 'print-order') {
                    printServiceOrderForm(Number(id));
                    return;
                  }
                  if (key === 'delete') {
                    if (order) requestDeletionFlow(order, load);
                    return;
                  }
                  if (key !== 'oil-change') return;
                  if (draft.client_type === 'counterparty') {
                    message.info('Замена масла — только для розничных заказов. Для юрлица используйте полный вид заказ-наряда.');
                    return;
                  }
                  const doSwitch = async () => {
                    if (!isNew && id) {
                      try {
                        await setOrderType(Number(id), 'oil_change');
                      } catch { /* вид локальный — переключаем в любом случае */ }
                      navigate(`/oil-change/${id}`);
                    } else {
                      navigate('/oil-change/new');
                    }
                  };
                  Modal.confirm({
                    title: 'Показать в виде замены масла',
                    content: 'Несохранённые изменения будут потеряны. Переключить вид?',
                    okText: 'Переключить', cancelText: 'Отмена',
                    onOk: doSwitch,
                  });
                },
              }}
            >
              <Button icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        </Col>
      </Row>

      {/* ── Информация о 1С-синхронизации */}
      {!isNew && (order?.external_1c_number || order?.external_1c_cash_receipt_number || order?.external_1c_parts_order_number || order?.external_1c_sale_number || order?.external_1c_service_sale_number || order?.external_1c_invoice_number || order?.external_1c_service_invoice_number) && (
        <Alert
          type="success"
          showIcon
          message={
            <Space wrap>
              <span>Синхронизирован с 1С</span>
              {order.external_1c_number && <Tag color="blue">Заказ-наряд № {order.external_1c_number}</Tag>}
              {order.external_1c_cash_receipt_number && <Tag color="green">ЧекККМ № {order.external_1c_cash_receipt_number}</Tag>}
              {order.external_1c_parts_order_number && <Tag color="purple">ЗН АвтоРегиум № {order.external_1c_parts_order_number}</Tag>}
              {order.external_1c_sale_number && (
                <Tag color="orange">
                  Реализация запчастей № {order.external_1c_sale_number}
                  {order.sale_posted === false ? ' (не проведена)' : ''}
                </Tag>
              )}
              {order.external_1c_service_sale_number && (
                <Tag color="orange">
                  Реализация услуг № {order.external_1c_service_sale_number}
                  {order.service_sale_posted === false ? ' (не проведена)' : ''}
                </Tag>
              )}
              {order.external_1c_invoice_number && (
                <Tag color="geekblue">Счёт-фактура запчастей № {order.external_1c_invoice_number}</Tag>
              )}
              {order.external_1c_service_invoice_number && (
                <Tag color="geekblue">Счёт-фактура услуг № {order.external_1c_service_invoice_number}</Tag>
              )}
            </Space>
          }
          description="Документы созданы в 1С. Для проведения — найдите их по номеру в 1С."
          action={
            <Button
              size="small"
              type="primary"
              onClick={() => {
                Modal.info({
                  title: 'Открыть в 1С',
                  content: (
                    <Space direction="vertical">
                      {order.external_1c_number && (
                        <>
                          <Text>Заказ-наряд (ИП Фомин):</Text>
                          <Text strong copyable>{order.external_1c_number}</Text>
                        </>
                      )}
                      {order.external_1c_cash_receipt_number && (
                        <>
                          <Text>ЧекККМ (АвтоРегиум):</Text>
                          <Text strong copyable>{order.external_1c_cash_receipt_number}</Text>
                        </>
                      )}
                      {order.external_1c_parts_order_number && (
                        <>
                          <Text>Заказ-наряд (АвтоРегиум, запчасти юрлица):</Text>
                          <Text strong copyable>{order.external_1c_parts_order_number}</Text>
                        </>
                      )}
                      {order.external_1c_sale_number && (
                        <>
                          <Text>Реализация (АвтоРегиум, марки юрлица):</Text>
                          <Text strong copyable>{order.external_1c_sale_number}</Text>
                          {order.sale_posted === false && <Text type="warning">Реализация не проведена — проверьте остатки в 1С.</Text>}
                        </>
                      )}
                      <Text type="secondary">Введите номер в поле поиска 1С (Ctrl+F) или найдите документ в журнале.</Text>
                    </Space>
                  ),
                  okText: 'Понятно',
                });
              }}
            >
              Открыть 1С
            </Button>
          }
          style={{ marginBottom: 12 }}
        />
      )}

      {/* ── Блок 1: Поля шапки */}
      <div style={{ marginBottom: 16, padding: '0 4px' }}>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item label="Автомобиль" style={{ marginBottom: 8 }}>
              <VehiclePicker
                make={draft.vehicle_make}
                model={draft.vehicle_model}
                legacyName={draft.vehicle}
                autoFocus={isNew}
                disabled={!canWrite}
                onChange={(v) => {
                  // Выбор из справочника/«Другое» — GUID отдаст backend при сохранении
                  const display = v.make ? [v.make, v.model].filter(Boolean).join(' ') : v.model;
                  update({
                    vehicle_make: v.make,
                    vehicle_model: v.model,
                    vehicle: display || null,
                    vehicle_id: null,
                  });
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item label="Госномер" style={{ marginBottom: 8 }}>
              <PlateInput
                value={draft.license_plate}
                onChange={(v) => update({ license_plate: v })}
                disabled={!canWrite}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item label="VIN" style={{ marginBottom: 8 }}>
              <Input
                placeholder="WDB..., JTNBE..."
                value={draft.vin ?? undefined}
                onChange={(e) => update({ vin: e.target.value || null })}
                disabled={!canWrite}
              />
            </Form.Item>
          </Col>

          <Col xs={12} md={4}>
            <Form.Item label="Пробег, км" style={{ marginBottom: 8 }}>
              <InputNumber
                min={0} value={draft.mileage ?? undefined}
                onChange={(v) => update({ mileage: v == null ? null : Number(v) })}
                style={{ width: '100%' }}
                disabled={!canWrite}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Клиент" style={{ marginBottom: 8 }}>
              <Select
                showSearch allowClear filterOption={false}
                placeholder="ФИО, название или ИНН"
                value={draft.client_id
                  ? `${draft.client_type}:${draft.client_id}`
                  : (draft.client_label ? '__current__' : undefined)}
                onChange={(k) => pickClient(k ?? null)}
                onSearch={loadClients}
                onDropdownVisibleChange={(v) => { if (v) loadClients(); }}
                loading={loadingClients}
                disabled={!canWrite}
                style={{ width: '100%' }}
                options={[
                  // Синтетическая опция: клиент есть в 1С, но нет локальной
                  // привязки (retail ФИО) — показываем label (§2А, rev. 2026-08-19)
                  ...(!draft.client_id && draft.client_label
                    ? [{ value: '__current__', label: draft.client_label, textLabel: draft.client_label }]
                    : []),
                  ...clientOptions.map(c => ({
                    value: `${c.type}:${c.id}`,
                    label: (
                      <Space>
                        <span>{c.label}</span>
                        <Tag color={c.type === 'individual' ? 'blue' : 'purple'}>
                          {c.type === 'individual' ? 'Физлицо' : 'Юрлицо'}
                        </Tag>
                      </Space>
                    ),
                    textLabel: `${c.label} (${c.type === 'individual' ? 'Физлицо' : 'Юрлицо'})`,
                  })),
                ]}
                optionLabelProp="textLabel"
              />
            </Form.Item>
          </Col>

          {!isNew && order && (
            <Col xs={24} md={4}>
              <Form.Item label="Создан" style={{ marginBottom: 8 }}>
                <Input
                  value={dayjs(order.created_at).format('DD.MM.YYYY HH:mm')}
                  readOnly
                  style={{ background: '#fafafa', color: '#666' }}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
      </div>

      <Divider style={{ margin: '16px 0' }} />

      {/* ── Блоки 2 и 3: Услуги и Запчасти */}
      {renderLinesTable('service', draft.services, totalServices)}

      <Divider style={{ margin: '16px 0' }} />

      {renderLinesTable('part', draft.parts, totalParts)}

      {/* ── Сканы документов, привязанные к ЗН */}
      {linkedScans.length > 0 && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <div style={{ marginBottom: 16, padding: '0 4px' }}>
            <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 8 }}>
              Сканы документов
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {linkedScans.map(s => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Link to={`/documents/${s.id}`}>
                    {s.title || `${s.type_name || 'Документ'} ${s.number ? '№ ' + s.number : ''}`.trim() || `Документ #${s.id}`}
                  </Link>
                  {s.date && <Text type="secondary" style={{ fontSize: 12 }}>{s.date}</Text>}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {s.page_count} стр.
                  </Text>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <Divider style={{ margin: '16px 0' }} />

      {/* ── Блок 4: Дополнительно */}
      <div style={{ marginBottom: 16, padding: '0 4px' }}>
        <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 8 }}>
          Дополнительно
        </Text>
        <Form.Item label="Рекомендации" style={{ marginBottom: 12 }}>
          <Input.TextArea
            rows={3} value={draft.notes}
            onChange={(e) => update({ notes: e.target.value })}
            disabled={!canWrite}
          />
        </Form.Item>

        <Row gutter={16} style={{ marginBottom: 12 }}>
          <Col xs={24} md={6}>
            <Form.Item label="Вид оплаты" style={{ marginBottom: 8 }}>
              <Select
                value={draft.payment_type}
                onChange={(v) => update({ payment_type: v as 'Касса' | 'Банк' })}
                options={[
                  { value: 'Касса', label: 'Касса' },
                  { value: 'Банк', label: 'Банк' },
                ]}
                disabled={!canWrite}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item style={{ marginBottom: 8 }}>
              <Checkbox
                checked={draft.card_payment}
                onChange={(e) => update({ card_payment: e.target.checked })}
                disabled={!canWrite}
              >Оплата картой</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Фотографии</div>
            <div style={{
              border: '1px dashed #d9d9d9', borderRadius: 8, padding: 16,
              textAlign: 'center', background: '#fafafa', color: '#999', minHeight: 100,
            }}>
              <PlusOutlined style={{ fontSize: 20 }} />
              <div style={{ marginTop: 6, fontSize: 12 }}>
                Перетащите файлы сюда или нажмите <code>+</code>
                <br />
                <i>(в разработке)</i>
              </div>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Документы</div>
            <div style={{
              border: '1px dashed #d9d9d9', borderRadius: 8, padding: 16,
              textAlign: 'center', background: '#fafafa', color: '#999', minHeight: 100,
            }}>
              <PlusOutlined style={{ fontSize: 20 }} />
              <div style={{ marginTop: 6, fontSize: 12 }}>
                Перетащите файлы сюда или нажмите <code>+</code>
                <br />
                <i>(в разработке)</i>
              </div>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Диагностический акт</div>
            <div style={{
              border: '1px dashed #d9d9d9', borderRadius: 8, padding: 16,
              textAlign: 'center', background: '#fafafa', color: '#999', minHeight: 100,
            }}>
              <PlusOutlined style={{ fontSize: 20 }} />
              <div style={{ marginTop: 6, fontSize: 12 }}>
                Прикрепить PDF / фото
                <br />
                <i>(в разработке)</i>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* ── Блок 5: Итого */}
      <div style={{ marginBottom: 16, padding: '0 4px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 0',
          borderTop: '1px solid #f0f0f0',
        }}>
          <Text strong style={{ fontSize: 16 }}>ИТОГО</Text>
          <Text strong style={{ fontSize: 16 }}>{grandTotal.toLocaleString('ru-RU')} ₽</Text>
        </div>
        <Space direction="vertical" style={{ width: '100%' }} size={4}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text>Услуги:</Text>
            <Text>{totalServices.toLocaleString('ru-RU')} ₽</Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text>Запчасти:</Text>
            <Text>{totalParts.toLocaleString('ru-RU')} ₽</Text>
          </div>
          {unreceivedSum > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text style={{ color: '#fa8c16' }}>Из них ожидает оприходования:</Text>
              <Text style={{ color: '#fa8c16' }}>{unreceivedSum.toLocaleString('ru-RU')} ₽</Text>
            </div>
          )}
        </Space>
      </div>

      {(() => {
        const master = markPickerKey ? draft.parts.find(l => l.key === markPickerKey) : null;
        return (
          <MarkPickerModal
            open={!!markPickerKey && !!master}
            productId={master?.external_1c_id ?? null}
            serviceOrderId={order?.id ?? null}
            onPick={(mark) => {
              if (markPickerKey) addManualChild(markPickerKey, mark);
              setMarkPickerKey(null);
            }}
            onCancel={() => setMarkPickerKey(null)}
          />
        );
      })()}

      {!isNew && order && (
        <PostAndPayModal
          open={postPayOpen}
          order={order}
          canPost={postingAvailable}
          canFiscalize={fiscalizeAvailable}
          canPrint={printAvailable}
          canInvoice={invoiceAvailable}
          onSyncCardPayment={canWrite ? async (cardPayment) => doSave(false, cardPayment) : undefined}
          onClose={(wasChanged) => {
            setPostPayOpen(false);
            if (wasChanged) window.location.reload();
          }}
        />
      )}

      <ShiftClosedModal
        open={shiftModalOpen}
        onClose={() => setShiftModalOpen(false)}
        onConfirm={() => {
          setShiftModalOpen(false);
          void doSave(true);
        }}
      />

      <Modal
        open={!!selectedMarkCode}
        onCancel={() => {
          setSelectedMarkCode(null);
          setSelectedMarkRawCode(null);
        }}
        footer={null}
        centered
        width={360}
      >        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 16 }}>
          {loadingMarkRawCode ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Загрузка полного кода...</div>
          ) : selectedMarkRawCode ? (
            <>
              <DataMatrixImage code={selectedMarkRawCode} scale={2} />
              <div style={{ marginTop: 12, wordBreak: 'break-all', fontSize: 12, userSelect: 'text' }}>
                {selectedMarkCode}
              </div>
            </>
          ) : (
            <div style={{ padding: 20, textAlign: 'center', color: '#ff4d4f' }}>
              Полный код марки не найден.
              <br />
              DataMatrix нельзя сгенерировать по обрезанному marking_code.
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ServiceOrderPage;
